/**
 * RefreshRateSelector - Contador de frecuencia DINÁMICO
 * Funciona con CUALQUIER frecuencia (15s, 30s, 1min, 2min, 5min)
 * Colores: Cian → Verde → Ámbar → Rojo (con parpadeo intenso)
 */
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Timer, Check, CaretDown, Lightning, ArrowsClockwise } from '@phosphor-icons/react';

const REFRESH_OPTIONS = [
  { value: 15000, label: '15 seg', description: 'Tiempo real', icon: '⚡' },
  { value: 30000, label: '30 seg', description: 'Recomendado', icon: '✓' },
  { value: 60000, label: '1 min', description: 'Estándar', icon: '○' },
  { value: 120000, label: '2 min', description: 'Ahorro datos', icon: '◐' },
  { value: 300000, label: '5 min', description: 'Mínimo', icon: '◑' },
];

const STORAGE_KEY = 'marketRefreshRate';

export const useRefreshRate = () => {
  const [refreshRate, setRefreshRate] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? parseInt(saved, 10) : 30000;
  });

  // Cada componente que use este hook tiene su propio estado local. Para que
  // TODOS (selector, polling, contadores...) se sincronicen cuando el usuario
  // cambia la frecuencia, escuchamos el evento 'refreshRateChanged' y el
  // 'storage' (por si se abre en otra pestaña).
  useEffect(() => {
    const onChange = (e) => {
      const next = e?.detail;
      if (Number.isFinite(next) && next > 0) setRefreshRate(next);
    };
    const onStorage = (e) => {
      if (e.key !== STORAGE_KEY || !e.newValue) return;
      const next = parseInt(e.newValue, 10);
      if (Number.isFinite(next) && next > 0) setRefreshRate(next);
    };
    window.addEventListener('refreshRateChanged', onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('refreshRateChanged', onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const updateRefreshRate = (rate) => {
    setRefreshRate(rate);
    localStorage.setItem(STORAGE_KEY, rate.toString());
    window.dispatchEvent(new CustomEvent('refreshRateChanged', { detail: rate }));
  };

  return { refreshRate, setRefreshRate: updateRefreshRate };
};

const RefreshRateSelector = ({ className = '', hasMarkets = true }) => {
  const { refreshRate, setRefreshRate } = useRefreshRate();
  const [isOpen, setIsOpen] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const [countdown, setCountdown] = useState(Math.ceil(refreshRate / 1000));
  const [isUpdating, setIsUpdating] = useState(false);
  const [progress, setProgress] = useState(0);
  const buttonRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const updateTimeoutRef = useRef(null);

  // Countdown timer - calcula basándose en el refreshRate actual
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = now - lastUpdate;
      const totalMs = refreshRate;
      const remainingMs = Math.max(0, totalMs - elapsed);
      const remainingSec = Math.ceil(remainingMs / 1000);
      
      // Calcular progreso de 0 a 100
      const currentProgress = Math.min(100, (elapsed / totalMs) * 100);
      
      setCountdown(remainingSec);
      setProgress(currentProgress);
      
      // Solo activar updating si hay mercados para actualizar
      if (remainingSec === 0 && !isUpdating && hasMarkets) {
        setIsUpdating(true);
        
        // Timeout de seguridad: si después de 10 segundos no hay respuesta, resetear
        if (updateTimeoutRef.current) clearTimeout(updateTimeoutRef.current);
        updateTimeoutRef.current = setTimeout(() => {
          setIsUpdating(false);
          setLastUpdate(Date.now());
          setProgress(0);
        }, 10000);
      } else if (remainingSec === 0 && !hasMarkets) {
        // Si no hay mercados, simplemente reiniciar el contador
        setLastUpdate(Date.now());
        setProgress(0);
      }
    }, 100);
    return () => {
      clearInterval(interval);
      if (updateTimeoutRef.current) clearTimeout(updateTimeoutRef.current);
    };
  }, [refreshRate, lastUpdate, isUpdating, hasMarkets]);

  // Escuchar actualizaciones reales de mercado
  useEffect(() => {
    const handleUpdate = () => {
      if (updateTimeoutRef.current) clearTimeout(updateTimeoutRef.current);
      setLastUpdate(Date.now());
      setIsUpdating(false);
      setProgress(0);
    };
    window.addEventListener('marketsUpdated', handleUpdate);
    return () => window.removeEventListener('marketsUpdated', handleUpdate);
  }, []);

  // Cuando cambia el refreshRate, reiniciar el contador
  useEffect(() => {
    setLastUpdate(Date.now());
    setProgress(0);
    setCountdown(Math.ceil(refreshRate / 1000));
  }, [refreshRate]);

  // Calcular posición del dropdown (alinear a la DERECHA del botón para que no se salga
  // por el borde derecho del viewport. El dropdown tiene w-64 = 256px)
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const DROPDOWN_WIDTH = 256; // w-64
      const MARGIN = 8;
      // Preferido: alinear con el borde derecho del botón
      let left = rect.right - DROPDOWN_WIDTH;
      // Clamp al viewport (mínimo 8px del borde izq, máximo 8px del derecho)
      left = Math.max(MARGIN, Math.min(left, window.innerWidth - DROPDOWN_WIDTH - MARGIN));
      setDropdownPos({
        top: rect.bottom + 8,
        left
      });
    }
  }, [isOpen]);

  // Colores dinámicos según el progreso (0-100%)
  const getColors = () => {
    if (progress < 50) {
      // 0-50%: CIAN
      return {
        text: 'text-gold-300',
        border: 'border-gold-500/50',
        bg: 'rgba(0,220,255,0.15)',
        shadow: '0 0 15px rgba(0,220,255,0.3)',
        glow: '0 0 8px rgba(0,220,255,0.7)',
        iconFilter: 'drop-shadow(0 0 6px rgba(0,220,255,0.7))'
      };
    } else if (progress < 70) {
      // 50-70%: VERDE BRILLANTE
      return {
        text: 'text-[#00ff6a]',
        border: 'border-[#00ff6a]/60',
        bg: 'rgba(0,255,106,0.15)',
        shadow: '0 0 18px rgba(0,255,106,0.4)',
        glow: '0 0 10px rgba(0,255,106,0.8)',
        iconFilter: 'drop-shadow(0 0 8px rgba(0,255,106,0.8))'
      };
    } else if (progress < 85) {
      // 70-85%: ÁMBAR/NARANJA
      return {
        text: 'text-gold-400',
        border: 'border-gold-500/70',
        bg: 'rgba(245,158,11,0.18)',
        shadow: '0 0 20px rgba(245,158,11,0.5)',
        glow: '0 0 12px rgba(245,158,11,0.9)',
        iconFilter: 'drop-shadow(0 0 10px rgba(245,158,11,0.9))'
      };
    } else {
      // 85-100%: ROJO
      return {
        text: 'text-red-400',
        border: 'border-red-500/80',
        bg: 'rgba(239,68,68,0.2)',
        shadow: '0 0 25px rgba(239,68,68,0.6)',
        glow: '0 0 15px rgba(239,68,68,1)',
        iconFilter: 'drop-shadow(0 0 12px rgba(239,68,68,1))'
      };
    }
  };

  const colors = getColors();
  
  // Parpadeo que aumenta de frecuencia cerca del 100%
  const shouldPulse = progress >= 70;
  const shouldFlash = progress >= 85;
  
  // Velocidad de parpadeo: más rápido cerca del 100%
  let pulseSpeed = '0.8s';
  if (progress >= 95) {
    pulseSpeed = '0.15s';
  } else if (progress >= 90) {
    pulseSpeed = '0.25s';
  } else if (progress >= 85) {
    pulseSpeed = '0.4s';
  } else if (progress >= 70) {
    pulseSpeed = '0.6s';
  }

  // ESTADO: ACTUALIZANDO - Pequeño y discreto
  if (isUpdating || countdown === 0) {
    return (
      <div className={`relative ${className}`}>
        <div 
          className="flex items-center gap-1.5 px-2 py-1 rounded-lg border border-[#00ff6a]/50"
          style={{
            background: 'rgba(0,255,106,0.1)',
          }}
        >
          <ArrowsClockwise 
            size={14} 
            weight="bold" 
            className="text-[#00ff6a] animate-spin"
          />
          <span className="text-[10px] font-bold text-[#00ff6a]">
            ...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {/* Botón principal COMPACTO */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-1.5 px-2 py-1 rounded-lg transition-all duration-200
          hover:scale-105 active:scale-95 border ${colors.border}
        `}
        style={{
          background: colors.bg,
        }}
        title="Configurar frecuencia de actualización"
      >
        {/* Icono pequeño */}
        <Timer 
          size={14} 
          weight="bold"
          className={`${colors.text}`}
        />
        
        {/* Contador compacto */}
        <span className={`text-xs font-mono font-bold tabular-nums ${colors.text}`}>
          {countdown}s
        </span>
        
        {/* Indicador modo rápido */}
        {refreshRate <= 15000 && (
          <Lightning 
            size={12} 
            weight="fill" 
            className="text-yellow-400"
          />
        )}
        
        <CaretDown 
          size={10} 
          weight="bold"
          className={`transition-all duration-300 ${isOpen ? 'rotate-180' : ''} ${colors.text} opacity-60`} 
        />
      </button>

      {/* Dropdown */}
      {isOpen && createPortal(
        <>
          <div 
            className="fixed inset-0 z-[9998]"
            onClick={() => setIsOpen(false)}
          />
          <div 
            className="fixed w-64 rounded-2xl overflow-hidden z-[9999]"
            style={{
              top: dropdownPos.top,
              left: dropdownPos.left,
              background: 'linear-gradient(180deg, rgba(15, 28, 50, 0.98) 0%, rgba(8, 16, 32, 1) 100%)',
              border: '2px solid rgba(0, 220, 255, 0.4)',
              boxShadow: '0 25px 60px rgba(0, 0, 0, 0.9), 0 0 40px rgba(0, 220, 255, 0.2)',
            }}
          >
            <div className="h-[3px] bg-gradient-to-r from-transparent via-gold-400 to-transparent" />
            
            <div className="px-4 py-3 border-b border-gold-500/30 bg-gold-500/5">
              <div className="flex items-center gap-2">
                <Timer size={18} className="text-gold-400" weight="bold" />
                <span className="text-sm font-black text-gold-300 uppercase tracking-wider">
                  Frecuencia
                </span>
              </div>
              <div className="text-[10px] text-gold-500/60 mt-0.5">
                Próxima actualización en {countdown}s ({Math.round(progress)}%)
              </div>
            </div>

            <div className="p-2">
              {REFRESH_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    setRefreshRate(option.value);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-3 rounded-xl transition-all duration-200 ${
                    refreshRate === option.value
                      ? 'bg-gradient-to-r from-gold-500/25 to-gold-600/15 border-2 border-gold-400/50 shadow-[0_0_15px_rgba(0,220,255,0.2)]'
                      : 'hover:bg-white/5 border-2 border-transparent hover:border-white/10'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className={`w-12 h-12 rounded-xl flex items-center justify-center font-mono font-black text-lg ${
                        refreshRate === option.value
                          ? 'bg-gold-500/30 text-gold-200 border-2 border-gold-400/60'
                          : 'bg-zinc-800/80 text-zinc-400 border-2 border-zinc-700/50'
                      }`}
                    >
                      {option.label.split(' ')[0]}
                    </div>
                    
                    <div className="text-left">
                      <div className={`text-base font-bold ${
                        refreshRate === option.value ? 'text-gold-200' : 'text-white'
                      }`}>
                        {option.label}
                      </div>
                      <div className={`text-xs ${
                        refreshRate === option.value ? 'text-gold-400/80' : 'text-zinc-500'
                      }`}>
                        {option.description}
                      </div>
                    </div>
                  </div>
                  
                  {refreshRate === option.value && (
                    <div className="w-8 h-8 rounded-full bg-gold-500/30 flex items-center justify-center border border-gold-400/50">
                      <Check size={18} className="text-gold-300" weight="bold" />
                    </div>
                  )}
                </button>
              ))}
            </div>
            
            <div className="px-4 py-2.5 border-t border-gold-500/20 bg-gold-500/5">
              <div className="text-[11px] text-zinc-400 text-center">
                Última: <span className="text-gold-400 font-mono">{new Date(lastUpdate).toLocaleTimeString('es-ES')}</span>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default RefreshRateSelector;
